import { share } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { BbTableService, BbTableSelectOption, TableRow } from '../../../services/bb-table.service';
import { DialogDefinition, OverlayService } from '../../../services/overlay.service';
import { User, Role } from './../../../interfaces/user';
import { Publication } from './../../../interfaces/publication';
import { BbFilterConfiguration } from '../../shared/bb-filters/bb-filters.component';
import { PublicationService } from '../../../services/publication.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { AbstractListComponent } from '../../shared/abstract-list.component';
import { LocationService } from '../../../services/location.service';
import { SapiService } from '../../../services/sapi.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FilterSetService } from '../../../services/filter-set.service';

@Component({
	selector: 'user-list',
	templateUrl: './user-list.component.html',
	styleUrls: ['./user-list.component.scss'],
	providers: [BbTableService]
})

export class UserListComponent extends AbstractListComponent implements OnInit {

	filterConfig: BbFilterConfiguration;
	hasExternalUsers: boolean;
	currentPublicationId: string;
	currentPublicationLabel: string;
	rolesData: any;
	putRight = false;

	constructor(
		protected bbTableService: BbTableService,
		protected sapiService: SapiService,
		protected router: Router,
		protected overlayService: OverlayService,
		protected translate: TranslateService,
		protected publicationService: PublicationService,
		protected locationService: LocationService,
		protected filtersetService: FilterSetService,
		protected authenticationService: AuthenticationService) {

		super(bbTableService, filtersetService);

		this.currentPublicationId = '';
		this.currentPublicationLabel = '';
		this.hasExternalUsers = false;
		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);

		this.listConfiguration = {
			name: 'user',
			basePath: '/publication/users/'
		};

		this.filterConfig = {
			search: true
		};
	}

	ngOnInit() {

		this.bbTableService
			.setSelect(BbTableSelectOption.single)
			.setLimit(15)
			.sort('createddate', 'desc')
			.linkToUrl();

		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			if (!publication) {
				return;
			}
			this.currentPublicationId = publication.id;
			this.currentPublicationLabel = publication.label || publication.name;
		}));

		this.subscriptions.add(this.publicationService.getRoles().subscribe((roles: Role[]) => {
			if (roles && roles.length) {
				this.rolesData = {};
				roles.forEach((role) => {
					this.rolesData['role' + role.id] = role.label || role.name;
				});
			}
		}));

		super.ngOnInit();
	}

	duplicateUser(row: TableRow) {
		this.router.navigate(['/publication/users/new'], { queryParams: { fromId: row.id } });
	}

	// override super doubleclick redirect:
	redirectToDetails(row: TableRow) {
		if (
			row && row.data && row.data.id && !row.data.externalId &&
			this.currentPublicationId && row.data.publicationid && row.data.publicationid === this.currentPublicationId
		) {
			this.router.navigate([this.listConfiguration.basePath, row.data.id]);
		}

	}

	mailAllUsers(): string {

		if (!this.rows) {
			return '';
		}

		let mailLink = 'mailto:?bcc=';
		this.selectedRows.forEach((row) => {
			if (row.data.email) {
				mailLink += encodeURIComponent(row.data.email) + ',';
			}
		});
		return mailLink;
	}

	deleteSingle(user: User) {
		let numPubs = 1;

		if (user && user.contextRoles) {
			numPubs = Object.keys(user.contextRoles).length;
		}

		let dialogContents = this.translate.instant('DELETE_ENTITY_CONFIRM', {
			entity: this.translate.instant('USER').toLowerCase()
		});

		if (numPubs > 1) {
			dialogContents += ' ' + this.translate.instant(
				(numPubs === 2 ? 'USER_IS_AUTHENTICATED_TO_X_OTHER_PUBLICATION' : 'USER_IS_AUTHENTICATED_TO_X_OTHER_PUBLICATIONS'), {
					x: (numPubs - 1)
				});
		}

		super.deleteSingle(user, dialogContents);
	}

	resetPassword(row: TableRow) {
		const user = row.data;
		if (user && user.id) {
			const dialog: DialogDefinition = {
				icon: 'refresh',
				title: this.translate.instant('RESETTING_PASSWORD'),
				contents: this.translate.instant('RESET_PASSWORD_CONFIRM', { email: user.email }),
				onConfirm: () => {

					const resetPasswordObservable = this.sapiService.getUrl(`/user/${user.id}/resetPassword`)
						.pipe(
							share()
						);
					resetPasswordObservable.subscribe(() => {
						this.overlayService.createToast(this.translate.instant('SUCCESSFULLY_RESET_PASSWORD_FOR', { id: user.id }), 'check');
					});

					return resetPasswordObservable;
				}
			};

			this.overlayService.createDialog(dialog);
		}
	}

	unlinkUser(user: User) {
		const dialog: DialogDefinition = {
			icon: 'unlink',
			title: this.translate.instant('UNLINKING_ENTITY', { id: user.id, entity: this.translate.instant('USER') }),
			contents: this.translate.instant('UNLINKING_USER_CONFIRM', { id: user.id, publication: this.currentPublicationLabel }),
			onConfirm: () => {

				const observable = this.sapiService.delete('user', user.id);

				observable.subscribe(() => {
					this.bbTableService.removeRows([user.id]);

					this.overlayService.createToast(this.translate.instant('USER_SUCCESSFULLY_UNLINKED',
						{
							id: user.id
						}), 'unlink');
				});

				return observable;
			}
		};

		this.overlayService.createDialog(dialog);
	}

	switchToUser(userHandle: string) {
		this.authenticationService.switchToUser(userHandle).subscribe((success: boolean) => {
			if (success) {
				location.reload();
			}
		});
	}

	showData(id: number) {
		const url = this.locationService.server + '/sapi/user/' + id + '/?sesstoken=' + this.sapiService.getSessionToken();
		const anchor = document.createElement('A');
		anchor.setAttribute('href', url);
		anchor.setAttribute('target', '_blank');

		const evt = document.createEvent('MouseEvents');
		evt.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
		anchor.dispatchEvent(evt);
	}
}
